import { ComponentPropsWithRef, ElementType, forwardRef, useMemo, useRef } from 'react'

import { twMerge } from 'tailwind-merge'

import { Link as RouterLink } from '@redwoodjs/router'

import { useMergeRefs } from 'src/hooks/use-merge-refs'

import Icon from '../Icon/Icon'

import { getLinkProps, isInternalLink, LinkActionProps } from './util'

type LinkElementType = Extract<ElementType, 'a' | 'button'>

type Props = {
  ariaLabel?: string
  className?: string
  children: React.ReactNode
} & LinkActionProps

const Link = forwardRef(
  <TElement extends LinkElementType = 'a'>(
    props: Props,
    propRef: ComponentPropsWithRef<TElement>['ref']
  ) => {
    const { ariaLabel, className: propClassName, children, externalHref, onClick } = props
    const className = twMerge(
      // TODO: remove important styling once we've eliminated all antd tables
      '!text-link inline cursor-pointer text-left hover:!underline focus:!underline focus-visible:!outline-1 !outline-black rounded-xs',
      propClassName
    )
    const internalRef = useRef<HTMLElement>(null)
    const ref = useMergeRefs(propRef || null, internalRef)

    const content = useMemo(
      () => (
        <>
          {children}
          {!!externalHref && (
            <>
              &nbsp;
              <Icon.FaArrowUpRightFromSquare />
            </>
          )}
        </>
      ),
      [children, externalHref]
    )

    const { rel, target } = getLinkProps(!!externalHref)

    if (isInternalLink(props)) {
      return (
        <RouterLink
          aria-label={ariaLabel}
          className={className}
          ref={ref}
          rel={rel}
          target={target}
          to={props.internalRoute}
        >
          {content}
        </RouterLink>
      )
    }

    const Element: LinkElementType = externalHref ? 'a' : 'button'
    return (
      <Element
        aria-label={ariaLabel}
        className={className}
        href={externalHref}
        onClick={onClick}
        ref={ref}
        rel={rel}
        target={target}
      >
        {content}
      </Element>
    )
  }
)

export default React.memo(Link)
